import { useState, useEffect } from 'react';
import ProductCard from '../components/ProductCard';
import './Shop.css';

const API_BASE = process.env.REACT_APP_API_URL || '/backend/api';

const CATEGORIES = ['All', 'Dress', 'Blouse', 'Fabric', 'Uniform', 'Gown', 'Accessory', 'General'];

const Shop = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');

  useEffect(() => {
    const fetchAll = async () => {
      try {
        // Load all sections for the store
        const res = await fetch(`${API_BASE}/products`);
        const data = await res.json();
        setProducts(data.map(p => ({ ...p, link: `#product-${p.id}` })));
      } catch (err) {
        console.error('Could not load products:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  const filtered = products
    .filter(p => {
      const matchCat = activeCategory === 'All' || p.category.toLowerCase() === activeCategory.toLowerCase();
      const matchSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (p.description || '').toLowerCase().includes(searchQuery.toLowerCase());
      return matchCat && matchSearch && p.in_stock;
    })
    .sort((a, b) => {
      if (sortBy === 'newest') return b.id - a.id;
      if (sortBy === 'oldest') return a.id - b.id;
      // Price sort: strip non-numeric
      const priceA = parseFloat(a.price.replace(/[^0-9.]/g, '')) || 0;
      const priceB = parseFloat(b.price.replace(/[^0-9.]/g, '')) || 0;
      if (sortBy === 'price_asc') return priceA - priceB;
      if (sortBy === 'price_desc') return priceB - priceA;
      return 0;
    });

  return (
    <main className="shop-page">
      {/* Shop Hero */}
      <section className="shop-hero">
        <div className="container">
          <span className="shop-eyebrow">Pompano Garment Manufacturing</span>
          <h1>Our Collection</h1>
          <p>Handcrafted pieces from our Lilongwe studio — ready to wear or made to order.</p>
        </div>
      </section>

      <div className="container shop-layout">
        {/* Filters */}
        <aside className="shop-sidebar">
          <h3 className="filter-heading">Category</h3>
          <ul className="category-list">
            {CATEGORIES.map(cat => (
              <li key={cat}>
                <button
                  className={`cat-btn ${activeCategory === cat ? 'active' : ''}`}
                  onClick={() => setActiveCategory(cat)}
                >
                  {cat}
                </button>
              </li>
            ))}
          </ul>
        </aside>

        {/* Products */}
        <section className="shop-main">
          {/* Toolbar */}
          <div className="shop-toolbar">
            <div className="search-wrap">
              <input
                type="text"
                className="shop-search"
                placeholder="Search products…"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </div>
            <select className="sort-select" value={sortBy} onChange={e => setSortBy(e.target.value)}>
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="price_asc">Price: Low to High</option>
              <option value="price_desc">Price: High to Low</option>
            </select>
            <span className="results-count">{filtered.length} item{filtered.length !== 1 ? 's' : ''}</span>
          </div>

          {/* Active filter tags */}
          {(activeCategory !== 'All' || searchQuery) && (
            <div className="active-filters">
              {activeCategory !== 'All' && (
                <span className="filter-tag">{activeCategory} <button onClick={() => setActiveCategory('All')}>×</button></span>
              )}
              {searchQuery && (
                <span className="filter-tag">"{searchQuery}" <button onClick={() => setSearchQuery('')}>×</button></span>
              )}
            </div>
          )}

          {loading ? (
            <div className="shop-loading">
              <div className="loading-dots"><span></span><span></span><span></span></div>
              <p>Loading collection…</p>
            </div>
          ) : filtered.length > 0 ? (
            <div className="products-grid">
              {filtered.map(product => <ProductCard key={product.id} {...product} />)}
            </div>
          ) : (
            <div className="shop-empty">
              <div className="empty-icon">✦</div>
              <h3>No products found</h3>
              <p>Try adjusting your filters or search term.</p>
              <button className="btn btn-primary" onClick={() => { setActiveCategory('All'); setSearchQuery(''); }}>
                Clear Filters
              </button>
            </div>
          )}
        </section>
      </div>

      {/* WhatsApp CTA */}
      <section className="shop-cta">
        <div className="container">
          <h2>Can't Find What You're Looking For?</h2>
          <p>We do custom orders — your fabric, your design, your size. Chat with us on WhatsApp.</p>
          <a
            href="https://wa.me/265 884996996?text=Hello%2C%20I%20would%20like%20to%20make%20a%20custom%20order"
            className="btn btn-primary"
            target="_blank"
            rel="noreferrer"
          >
            WhatsApp Us
          </a>
        </div>
      </section>
    </main>
  );
};

export default Shop;
